/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { Hidden, Typography2, typography2 } from '@coursera/cds-core';

import usePrevious from 'bundles/common/hooks/usePrevious';
import ExpandableSearchFilterGroup from 'bundles/search-common/components/filters/ExpandableSearchFilterGroup';
import SearchFilterPlaceholder from 'bundles/search-common/components/filters/SearchFilterPlaceholder';
import SkipToResultsLink from 'bundles/search-common/components/filters/SkipToResultsLink';
import useSearchFilterConfigs from 'bundles/search-common/components/filters/useSearchFilterConfigs';
import { SEARCH_FILTERS_INDEX_ID, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData } from 'bundles/search-common/types';
import { getShouldUseRemoveFilterHack } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { createFilterKeyToOptionsMap } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

type Props = {
  eventingData: SearchPageEventingData;
  shouldShowSectionTitle: boolean;
  onFilterValueChange?: () => void;
  isMobile?: boolean;
};

const styles = {
  placeholderContainer: css`
    padding-top: 30px;
  `,
  filterTitle: css`
    ${typography2.subtitleLarge}
    margin-bottom: 32px;
  `,
  skipToResultsForNoTitle: css`
    position: absolute;
    top: -8px;
  `,
};

export const InlineSearchFilters = (props: Props): React.ReactElement | null => {
  const { eventingData, shouldShowSectionTitle, onFilterValueChange, isMobile } = props;
  const { filterGroupConfigs, filtersListOrder } = useSearchFilterConfigs();
  const { getSearchResults } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const isConsumerSearch = useIsConsumerSerp();
  const isEQP = useIsEqp();
  const removeFilterHack = (isConsumerSearch || isEQP) && getShouldUseRemoveFilterHack();
  const { facets: allFilters } =
    getSearchResults(removeFilterHack ? SEARCH_MAIN_INDEX_ID : SEARCH_FILTERS_INDEX_ID) ?? {};

  const { facets, facetFilters } = searchData || {};
  const previousFacetFilters = usePrevious(facetFilters);

  const filterKeyToOptionsMap = React.useMemo(
    () => createFilterKeyToOptionsMap(facets, facetFilters, filtersListOrder, allFilters),
    [allFilters, facetFilters, facets, filtersListOrder]
  );

  // saving previous filters to prevent from showing placeholder and close modal
  // this will be refactored out in https://coursera.atlassian.net/browse/SEARCH-256
  const [previousFilters, setPreviousFilters] = useState(filterKeyToOptionsMap);
  useEffect(() => {
    if (
      filterKeyToOptionsMap &&
      Object.keys(filterKeyToOptionsMap).length &&
      previousFacetFilters?.length !== facetFilters?.length
    ) {
      setPreviousFilters(filterKeyToOptionsMap);
    }
  }, [facetFilters?.length, filterKeyToOptionsMap, previousFacetFilters?.length]);

  const safeFilterKeyToOptionsMap =
    filterKeyToOptionsMap && Object.keys(filterKeyToOptionsMap).length ? filterKeyToOptionsMap : previousFilters;

  // Filters are loading if 'filters' (from Algolia) is undefined or is an empty object
  // and filtersList (static list of filters) is defined (which means we are expecting filter data from Algolia).
  const filtersLoading = !!filtersListOrder && !Object.keys(safeFilterKeyToOptionsMap ?? {}).length;
  if (filtersLoading) {
    return (
      <div>
        {Array.from(Array(7)).map((_item, index) => {
          // eslint-disable-next-line react/no-array-index-key
          return <SearchFilterPlaceholder key={index} isCompact={true} />;
        })}
      </div>
    );
  }
  return (
    <Hidden smDown>
      <div css={!shouldShowSectionTitle && styles.skipToResultsForNoTitle}>
        <SkipToResultsLink isMobile={isMobile} />
      </div>
      {shouldShowSectionTitle && (
        <Typography2 component="h2" css={styles.filterTitle}>
          {_t('Filter by')}
        </Typography2>
      )}

      {filterGroupConfigs
        .filter((group) => !!safeFilterKeyToOptionsMap[group.property]?.length)
        .map((group, i) => (
          <ExpandableSearchFilterGroup
            key={group.property}
            groupConfig={group}
            groupValues={safeFilterKeyToOptionsMap[group.property] ?? []}
            eventingData={{
              searchIndex: eventingData.searchIndex,
              searchIndexPosition: eventingData.searchIndexPosition,
              filtersApplied: eventingData.filtersApplied,
            }}
            onFilterValueChange={() => onFilterValueChange?.()}
            groupShouldHaveMinHeight={i === filterGroupConfigs.length - 1}
          />
        ))}
    </Hidden>
  );
};

export default InlineSearchFilters;
